import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  RiCloseLine,
  RiAddLine,
  RiDeleteBin6Line,
  RiCheckboxCircleFill,
  RiCheckLine,
  RiUploadCloud2Line,
} from '@remixicon/react';
import { toast } from 'sonner';
import AddCustomColorModal from './AddCustomColorModal';
import { isMfaCancelled } from '../hooks/MfaStepUpGate';

const PRESET_COLORS = [
  '#EF4444', // red
  '#F97316', // orange
  '#EAB308', // yellow
  '#22C55E', // green
  '#14B8A6', // teal
  '#3B82F6', // blue
  '#6366F1', // indigo
  '#A855F7', // purple
  '#EC4899', // pink
  '#FFFFFF', // white
  '#9CA3AF', // gray
  '#000000', // black
  '#A3E635', // lime
];

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getFileExt = (name = '') => {
  const ext = name.split('.').pop();
  return ext ? ext.toUpperCase().slice(0, 4) : 'FILE';
};

const FileTypeIcon = ({ ext }) => (
  <div className="relative w-10 h-12 flex-shrink-0">
    <svg viewBox="0 0 40 48" className="w-full h-full" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M4 4a4 4 0 0 1 4-4h20l12 12v32a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4V4Z"
        fill="#F3F4F6"
        stroke="#D1D5DB"
        strokeWidth="1"
      />
      <path d="M28 0l12 12h-8a4 4 0 0 1-4-4V0Z" fill="#E5E7EB" />
    </svg>
    <span className="absolute bottom-1 left-1 right-1 text-center text-[8px] font-bold tracking-wider text-white bg-gray-400 rounded-sm py-[1px]">
      {ext}
    </span>
  </div>
);

// S3 keys are stored with a UUID prefix (orgId) baked into the filename
// to avoid collisions — e.g. `logos/94d7...cf5fb9_logo.png`. That
// prefix is bookkeeping, not something a user typed, so strip it
// before display. Leaves untagged filenames untouched.
const UUID_PREFIX_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_/i;
const filenameFromUrl = (url = '') => {
  try {
    const path = url.split('?')[0];
    const last = path.split('/').pop();
    return decodeURIComponent(last || '').replace(UUID_PREFIX_RE, '');
  } catch {
    return '';
  }
};

const UpdateBrandingDrawer = ({ open, onClose, initialValues = {}, onSubmit }) => {
  const fileInputRef = useRef(null);
  const [color, setColor] = useState('#A3E635');
  const [customColors, setCustomColors] = useState([]);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [editColors, setEditColors] = useState(false);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setColor(initialValues.color || '#A3E635');
      setCustomColors(initialValues.customColors || []);
      setLogoFile(initialValues.logoFile || null);
      setLogoPreview(initialValues.logoPreview || '');
      setEditColors(false);
      setSaving(false);
    }
  }, [
    open,
    initialValues.color,
    initialValues.customColors,
    initialValues.logoFile,
    initialValues.logoPreview,
  ]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setLogoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const payload = { color, customColors, logoFile, logoPreview };
      await onSubmit?.(payload);
      onClose();
      toast.success('Branding updated successfully', { position: 'bottom-right' });
    } catch (err) {
      if (isMfaCancelled(err)) return; // user closed the step-up modal — silent
      toast.error(err?.message || 'Failed to update branding', { position: 'bottom-right' });
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <>
      <div
        className={`fixed inset-0 z-[9998] bg-black/40 transition-opacity duration-300 ${
          open ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />
      <div
        className={`fixed top-0 right-0 bottom-0 z-[9999] w-full sm:w-[560px] lg:w-[640px] bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-out ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between px-6 pt-5 pb-4">
          <h3 className="text-lg font-semibold text-primary-700">Update branding</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <RiCloseLine className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 pb-6 space-y-6">
          {/* Brand Color */}
          <div>
            <div className="flex items-center justify-between mb-2.5">
              <label className="text-sm font-medium text-gray-700">
                Choose Brand Color<span className="text-red-500 ml-0.5">*</span>
              </label>
              <button
                type="button"
                onClick={() => setEditColors((v) => !v)}
                className="text-sm font-medium text-primary-700 hover:underline"
              >
                {editColors ? 'Done' : 'Edit'}
              </button>
            </div>
            <div className="border border-gray-200 rounded-2xl p-4 space-y-4">
              <div className="flex flex-wrap gap-3">
                {[...PRESET_COLORS, ...customColors].map((c) => {
                  const isSelected = color.toLowerCase() === c.toLowerCase();
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setColor(c)}
                      className={`w-7 h-7 rounded-full transition-all ${
                        isSelected
                          ? 'ring-2 ring-offset-2 ring-gray-400'
                          : 'hover:scale-110'
                      } ${c === '#FFFFFF' ? 'border border-gray-300' : ''}`}
                      style={{ backgroundColor: c }}
                      aria-label={`Select color ${c}`}
                    />
                  );
                })}
              </div>
              <button
                type="button"
                onClick={() => setColorPickerOpen(true)}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-full bg-indigo-50 text-sm font-medium text-gray-800 hover:bg-indigo-100 transition-colors"
              >
                <RiAddLine className="w-4 h-4" />
                Add custom color
              </button>
            </div>
          </div>

          {/* Logo Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2.5">
              Upload Brokerage Logo<span className="text-red-500 ml-0.5">*</span>
            </label>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
            />

            {logoFile || logoPreview ? (
              (() => {
                const displayName = logoFile?.name || filenameFromUrl(logoPreview) || 'logo';
                const displaySize = logoFile?.size ? formatSize(logoFile.size) : '';
                return (
                  <div className="flex items-center gap-3 p-3 border border-gray-200 rounded-xl">
                    <FileTypeIcon ext={getFileExt(displayName)} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{displayName}</p>
                      <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1.5">
                        {displaySize && (
                          <>
                            {displaySize}
                            <span className="text-gray-300">·</span>
                          </>
                        )}
                        <RiCheckboxCircleFill className="w-3.5 h-3.5 text-green-500" />
                        <span>Completed</span>
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleRemoveLogo}
                      className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                      aria-label="Remove logo"
                    >
                      <RiDeleteBin6Line className="w-4 h-4" />
                    </button>
                  </div>
                );
              })()
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full flex flex-col items-center justify-center gap-4 py-10 border-2 border-dashed border-gray-200 rounded-xl hover:border-gray-300 transition-colors"
              >
                <RiUploadCloud2Line className="w-7 h-7 text-gray-400" />
                <p className="text-sm text-black">Choose a file</p>
                <p className="text-xs text-gray-500">JPEG, PNG, and SVG, up to 2 MB.</p>
                <span className="mt-1 text-sm text-gray-900 underline-offset-2 rounded-full py-1 px-2 hover:underline border border-gray-200">
                  Browse File
                </span>
              </button>
            )}
          </div>

          {/* Logo Preview — surround with the picked brand color so the
              admin sees the logo against the real in-app backdrop
              (matches the onboarding preview at Onboarding.jsx:1073).
              Falls back to a neutral gray when no color is picked yet. */}
          {logoPreview && (
            <div>
              <p className="text-xs font-medium tracking-wider text-gray-500 uppercase mb-2">
                Logo Preview
              </p>
              <div
                className="rounded-2xl p-8 flex items-center justify-center min-h-[180px] transition-colors"
                style={{ backgroundColor: color || '#F3F4F6' }}
              >
                <img
                  src={logoPreview}
                  alt="Logo preview"
                  className="max-h-32 max-w-full object-contain"
                />
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className={`w-full flex items-center justify-center gap-2 py-3 rounded-full text-sm font-medium transition-colors ${
              saving
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-primary-700 text-white hover:bg-primary-800'
            }`}
          >
            <RiCheckLine className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save changes'}
          </button>
        </div>
      </div>

      <AddCustomColorModal
        open={colorPickerOpen}
        onClose={() => setColorPickerOpen(false)}
        initialColor={color}
        onAdd={(c) => {
          setCustomColors((prev) => (prev.includes(c) ? prev : [...prev, c]));
          setColor(c);
        }}
      />
    </>,
    document.body
  );
};

export default UpdateBrandingDrawer;
